// ---------------------------------------------------------------------------
// ValuePickerModal -- Step 2: Choose Filter Values
// ---------------------------------------------------------------------------
// Modal for selecting specific values for a chosen dimension.
// Supports three modes:
//   - Short list (≤5 values) - show all, no search
//   - Long list (>5 values) - top 5 by count + local search filter
//   - Search mode (brands, teams) - search-as-you-type via API
// ---------------------------------------------------------------------------

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Check, Loader2, Search } from 'lucide-react';
import type { ValueCountsData } from '@/services/v11/funnelService';
import { getPlatformIcon } from '@/components/v11/shared/platformIcons';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ValueOption {
  readonly value: string | number;
  readonly label: string;
  /** Optional platform identifier for channel options (e.g., 'instagram', 'tiktok') */
  readonly platform?: string;
  /** Optional count to display as right-aligned badge (alternative to valueCounts lookup) */
  readonly count?: number;
  /** Optional badge pill (e.g., 'Team', 'Player') with color */
  readonly badge?: { readonly text: string; readonly color: string };
}

interface ValuePickerModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly dimensionLabel: string;
  readonly values: readonly ValueOption[];
  readonly isLoading?: boolean;
  readonly searchMode?: boolean;
  readonly onSearch?: (query: string) => void;
  readonly isSearching?: boolean;
  readonly onApply: (selectedValues: (string | number)[], selectedLabels: string[]) => void;
  readonly valueCounts?: ValueCountsData;
  readonly isLoadingCounts?: boolean;
  /** Pre-selected values when editing an existing filter */
  readonly initialSelected?: readonly (string | number)[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TOP_N = 5;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCount(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return count.toLocaleString();
}

function sortByCount(
  values: readonly ValueOption[],
  valueCounts: ValueCountsData | undefined
): readonly ValueOption[] {
  if (!valueCounts) return values;
  return [...values].sort((a, b) => {
    const countA = valueCounts[String(a.value)] ?? 0;
    const countB = valueCounts[String(b.value)] ?? 0;
    return countB - countA;
  });
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function OptionRow({
  option,
  isSelected,
  count,
  isLoadingCounts,
  onToggle,
  dimensionKey,
}: {
  readonly option: ValueOption;
  readonly isSelected: boolean;
  readonly count: number | undefined;
  readonly isLoadingCounts: boolean;
  readonly onToggle: (value: string | number) => void;
  readonly dimensionKey?: string;
}) {
  // Get platform icon: from option.platform (channels) or from label when dimension is "Platform"
  const platformConfig = option.platform
    ? getPlatformIcon(option.platform)
    : dimensionKey === 'Platform'
      ? getPlatformIcon(option.label)
      : null;
  // Use option.count if provided, otherwise use valueCounts lookup
  const displayCount = option.count ?? count;

  return (
    <button
      onClick={() => onToggle(option.value)}
      className={cn(
        'w-full p-3 rounded-lg border transition-all',
        'bg-v11-surface border-v11-border',
        'hover:border-indigo-500/40 hover:bg-indigo-500/5',
        'flex items-center gap-3'
      )}
    >
      <div
        className={cn(
          'w-5 h-5 shrink-0 rounded border-2 flex items-center justify-center transition-all',
          isSelected ? 'bg-indigo-500 border-indigo-500' : 'border-v11-border'
        )}
      >
        {isSelected && <Check className="h-3 w-3 text-white" />}
      </div>

      {/* Platform icon (for channels) */}
      {platformConfig && (
        <span
          className="shrink-0 flex items-center justify-center"
          style={{ color: platformConfig.color }}
          title={platformConfig.name}
        >
          {platformConfig.icon}
        </span>
      )}

      <span className="flex-1 text-left text-sm font-medium text-v11-text-primary truncate min-w-0">
        {option.label}
      </span>

      {option.badge && (
        <span
          className="shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold border"
          style={{ color: option.badge.color, borderColor: option.badge.color + '40', backgroundColor: option.badge.color + '15' }}
        >
          {option.badge.text}
        </span>
      )}

      {!isLoadingCounts && displayCount !== undefined && (
        <span className="shrink-0 text-xs font-medium text-indigo-400 tabular-nums">
          {formatCount(displayCount)}
        </span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ValuePickerModal({
  open,
  onClose,
  dimensionLabel,
  values,
  isLoading = false,
  searchMode = false,
  onSearch,
  isSearching = false,
  onApply,
  valueCounts,
  isLoadingCounts = false,
  initialSelected,
}: ValuePickerModalProps) {
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  // Reset state when modal opens/closes, pre-select values when editing
  useEffect(() => {
    if (open && initialSelected && initialSelected.length > 0) {
      setSelected(new Set(initialSelected));
    } else if (!open) {
      setSelected(new Set());
      setSearchQuery('');
    }
  }, [open, initialSelected]);

  // Debounced search for API search-mode dimensions
  useEffect(() => {
    if (!searchMode || !onSearch || searchQuery.length < 2) return;

    const timer = setTimeout(() => {
      onSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, searchMode, onSearch]);

  const handleToggle = useCallback((value: string | number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  }, []);

  const handleApply = () => {
    if (selected.size > 0) {
      const selectedValues = Array.from(selected);
      const selectedLabels = selectedValues.map(
        v => values.find(opt => opt.value === v)?.label ?? String(v)
      );
      onApply(selectedValues, selectedLabels);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  // Determine if this is a long list that needs top-5 + search
  const isLongList = !searchMode && values.length > TOP_N;

  // Sort values by count (highest first)
  const sortedValues = useMemo(() => sortByCount(values, valueCounts), [values, valueCounts]);

  // Top 5 values (by count)
  const topValues = useMemo(
    () => (isLongList ? sortedValues.slice(0, TOP_N) : sortedValues),
    [isLongList, sortedValues]
  );

  // Filtered values when searching in long list or search-mode with pre-loaded values
  const filteredValues = useMemo(() => {
    const hasLocalFilter = isLongList || (searchMode && values.length > 0);
    if (!hasLocalFilter || searchQuery.length === 0) return [];
    const q = searchQuery.toLowerCase();
    return sortedValues.filter(opt => opt.label.toLowerCase().includes(q));
  }, [isLongList, searchMode, values.length, searchQuery, sortedValues]);

  // Show filtered results or top/all values
  const showingFiltered =
    (isLongList || (searchMode && values.length > 0)) && searchQuery.length > 0;
  const displayValues = showingFiltered ? filteredValues : topValues;

  return (
    <Dialog open={open} onOpenChange={handleCancel}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-v11-text-primary flex items-center gap-2">
            Select Values - {dimensionLabel}
            {isLoadingCounts && <Loader2 className="h-4 w-4 animate-spin text-indigo-400" />}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Pick values for the selected dimension
          </DialogDescription>
        </DialogHeader>

        {/* Search input - API search mode (brand/team/channel/post/hashtag) */}
        {searchMode && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-v11-text-muted" />
            <Input
              placeholder={`Search ${dimensionLabel.toLowerCase()}...`}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9"
              autoFocus
            />
          </div>
        )}

        {/* Search input - local filter for long lists */}
        {isLongList && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-v11-text-muted" />
            <Input
              placeholder={`Search all ${values.length} options...`}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        )}

        {/* Section label */}
        {isLongList && !showingFiltered && (
          <div className="text-xs uppercase tracking-wider text-v11-text-muted font-semibold">
            Top {TOP_N} by count
          </div>
        )}
        {showingFiltered && (
          <div className="text-xs uppercase tracking-wider text-v11-text-muted font-semibold">
            {filteredValues.length} result{filteredValues.length !== 1 ? 's' : ''} found
          </div>
        )}

        <div className="space-y-2 max-h-[60vh] overflow-y-auto overflow-x-hidden">
          {isLoading || isSearching ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-v11-text-muted" />
              <span className="ml-2 text-sm text-v11-text-muted">Loading options...</span>
            </div>
          ) : searchMode && searchQuery.length < 2 && values.length === 0 ? (
            <div className="text-center py-8 text-sm text-v11-text-muted">
              Type at least 2 characters to search.
            </div>
          ) : displayValues.length === 0 && showingFiltered ? (
            <div className="text-center py-8 text-sm text-v11-text-muted">
              No matches for &ldquo;{searchQuery}&rdquo;
            </div>
          ) : displayValues.length === 0 ? (
            <div className="text-center py-8 text-sm text-v11-text-muted">
              No options available for this dimension.
            </div>
          ) : (
            displayValues.map(option => (
              <OptionRow
                key={option.value}
                option={option}
                isSelected={selected.has(option.value)}
                count={valueCounts?.[String(option.value)]}
                isLoadingCounts={isLoadingCounts}
                onToggle={handleToggle}
                dimensionKey={dimensionLabel}
              />
            ))
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleApply}
            disabled={selected.size === 0}
            className="bg-indigo-500 hover:bg-indigo-600"
          >
            Apply Filter ({selected.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
